import { Button, ButtonColors } from '@chiroup/components/Button';
import ReportPrint from '@chiroup/components/report-print/ReportPrint';
import { resolveLetterheadLogoUrl } from '@chiroup/components/report-print/resolveLetterheadLogoUrl';
import { ConditionReportPrintData } from '@chiroup/core/types/ReportPrint.type';
import dayjs from 'dayjs';
import React, { useEffect, useMemo } from 'react';
import { useQuery } from 'react-query';
import { Link, useParams } from 'react-router-dom';
import testPatientService from '../../../services/testPatient.service';

const errorMessage = (error: unknown) =>
  error instanceof Error ? error.message : 'The request failed. Please try again.';

type Props = {
  clinicId: number;
};

/**
 * Renders a test patient's condition report exactly as the ChiroUp report page
 * does — the same `ReportPrint` markup, on-screen preview and print copy — so
 * the printed PDF matches what a clinic would produce.
 */
const ClinicTestPatientReport: React.FC<Props> = ({ clinicId }) => {
  const { reportId } = useParams();
  const { data, isLoading, isError, error } = useQuery(
    ['clinicTestReportView', clinicId, reportId],
    () => testPatientService.reportView(clinicId, Number(reportId)),
    { refetchOnWindowFocus: false, enabled: !!reportId }
  );

  useEffect(() => {
    if (!data) return;

    const originalTitle = document.title;
    // The browser's print dialog seeds the PDF file name from the document
    // title, the same way the ChiroUp report page does.
    const beforePrintHandler = () => {
      document.title = `${data.exercisePlan ? 'EP' : 'CR'}-${data.patientName.replace(
        /\s+/g,
        '-'
      )}-${dayjs().format('M-D-YYYY')}`;
    };
    const afterPrintHandler = () => {
      document.title = originalTitle;
    };

    window.addEventListener('beforeprint', beforePrintHandler);
    window.addEventListener('afterprint', afterPrintHandler);

    return () => {
      window.removeEventListener('beforeprint', beforePrintHandler);
      window.removeEventListener('afterprint', afterPrintHandler);
    };
  }, [data]);

  const printProps = useMemo(() => {
    if (!data) return null;

    const report: ConditionReportPrintData = data;

    return {
      patientEmail: report.patientEmail,
      patientPhone: report.patientPhone,
      language: report.language,
      clinicName: report.clinicName,
      summary: report.summary ?? undefined,
      patientName: report.patientName,
      reportDate: report.reportDate,
      examFindings: report.examFindings ?? undefined,
      conditions: report.conditions,
      treatments: report.treatments,
      treatmentOptions: report.treatmentOptions,
      visits: report.visits,
      goalPercent: report.goalPercent,
      additionalGoals: report.additionalGoals ?? undefined,
      advice: report.advice,
      adviceOptions: report.adviceOptions,
      customSections: report.customSections,
      exercises: report.exercises,
      exercisePlan: report.exercisePlan,
      chartCopy: report.chartCopy,
      providerName: report.providerName,
      diagnosis: report.diagnosis,
      primaryColor: report.primaryColor,
      clinicId,
      logoUrl: resolveLetterheadLogoUrl(report.logo),
      locationInfo: report.locationInfo,
    };
  }, [data, clinicId]);

  return (
    <>
      <div className="print:hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 p-6">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold">
              {data?.exercisePlan ? 'Exercise plan' : 'Condition report'}
              {data ? ` for ${data.patientName}` : ''}
            </h2>
            <p className="text-sm text-muted-foreground">
              Report {reportId}
              {data?.reportDate ? ` · ${data.reportDate}` : ''}
            </p>
          </div>
          <div className="flex gap-3">
            <Link to={`/clinics/${clinicId}/test-patient`}>
              <Button text="Back to test patient" color={ButtonColors.plainWithBorder} />
            </Link>
            <Button text="Print" disabled={!printProps} onClick={() => window.print()} />
          </div>
        </div>
        {isLoading && (
          <p className="px-6" role="status">
            Loading report…
          </p>
        )}
        {isError && (
          <p className="px-6 text-red-600 dark:text-red-400" role="alert">
            Unable to load the report. {errorMessage(error)}
          </p>
        )}
        {!!printProps && (
          <div className="flex p-0 items-start justify-center w-full report-print-preview min-h-full relative inset-0 transform scale-50 sm:scale-75 md:scale-100 origin-top">
            <ReportPrint {...printProps} printPreview />
          </div>
        )}
      </div>
      {!!printProps && <ReportPrint {...printProps} />}
    </>
  );
};

export default ClinicTestPatientReport;
